import type { Editor as TiptapEditor } from "@tiptap/react"
import type { Node as ProseMirrorNode } from "@tiptap/pm/model"

import type { QnrEdit } from "features/Assistant/types"

/**
 * The questionnaire editor's lines: the text of each top-level paragraph, so
 * line n is `editorLines(editor)[n - 1]`. This is the numbering the editor's
 * `scrollToLine` and the assistant's `QnrEdit` use.
 */
export const editorLines = (editor: TiptapEditor): string[] => {
  const lines: string[] = []
  editor.state.doc.forEach((node) => {
    lines.push(node.textContent)
  })
  return lines
}

/**
 * The lines the editor's selection covers (1-based, inclusive), or undefined
 * when nothing is selected (a cursor only). A selection that ends at the very
 * start of a line does not take that line in.
 */
export const selectionLines = (
  editor: TiptapEditor
): { fromLine: number; toLine: number } | undefined => {
  const { doc, selection } = editor.state
  if (selection.empty || doc.childCount === 0) return undefined
  const last = doc.childCount - 1
  const fromIndex = Math.min(doc.resolve(selection.from).index(0), last)
  const $to = doc.resolve(selection.to)
  let toIndex: number
  if ($to.depth === 0) {
    // Between two lines (or at the end): the line before
    toIndex = $to.index(0) - 1
  } else {
    toIndex = $to.index(0)
    if ($to.parentOffset === 0 && toIndex > fromIndex) toIndex -= 1
  }
  toIndex = Math.max(fromIndex, Math.min(toIndex, last))
  return { fromLine: fromIndex + 1, toLine: toIndex + 1 }
}

const hasMarks = (node: ProseMirrorNode): boolean => {
  let marked = false
  node.descendants((child) => {
    if (child.marks.length > 0) marked = true
    return !marked
  })
  return marked
}

/** The lines (1-based) that carry any mark: bold, italic, a comment. */
export const linesWithMarks = (editor: TiptapEditor): Set<number> => {
  const marked = new Set<number>()
  editor.state.doc.forEach((node, _offset, index) => {
    if (hasMarks(node)) marked.add(index + 1)
  })
  return marked
}

/**
 * Whether the edit's lines in `doc` still equal its `oldLines` (an insert:
 * whether its place is still in the document). Reads only the edit's lines.
 */
const editMatches = (doc: ProseMirrorNode, edit: QnrEdit): boolean => {
  const { fromLine, toLine, oldLines } = edit
  if (!Number.isInteger(fromLine) || !Number.isInteger(toLine)) return false
  if (fromLine < 1 || toLine < fromLine - 1 || toLine > doc.childCount) return false
  if (oldLines.length !== toLine - fromLine + 1) return false
  return oldLines.every((line, i) => doc.child(fromLine - 1 + i).textContent === line)
}

/**
 * Whether none of `edits` still matches the editor, so applying them would
 * change nothing. Cheap: it reads only the edits' own lines (a proposal card
 * asks on every render).
 */
export const noEditMatches = (editor: TiptapEditor, edits: QnrEdit[]): boolean => {
  const { doc } = editor.state
  return edits.length > 0 && edits.every((edit) => !editMatches(doc, edit))
}
